import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'

// Renders GitHub-flavoured markdown (incl. tables) via remark-gfm, hand-styled for a
// clean, compact look. Shared by the AI chat drawer and the asset AI-analysis cards.
// remark-breaks is intentionally NOT used (it breaks GFM table detection).
export function Markdown({ children }: { children: string }) {
  return (
    <div className="text-sm leading-relaxed text-text-light dark:text-text-dark break-words space-y-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // ── Text ──────────────────────────────────────────────────────────
          p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
          strong: ({ children }) => (
            <strong className="font-semibold text-text-light dark:text-text-dark">{children}</strong>
          ),
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ children, href }) => {
            const cls =
              'text-primary dark:text-secondary font-medium underline decoration-primary/30 hover:decoration-primary underline-offset-2'
            // Internal links (e.g. linkified /assets/<id>) navigate client-side in the
            // same tab — no full reload. External links open in a new tab.
            if (href && href.startsWith('/')) {
              return (
                <Link to={href} className={cls}>
                  {children}
                </Link>
              )
            }
            return (
              <a href={href} target="_blank" rel="noreferrer" className={cls}>
                {children}
              </a>
            )
          },
          hr: () => <hr className="my-3 border-border-light dark:border-border-dark" />,

          // ── Headings ─────────────────────────────────────────────────────
          h1: ({ children }) => (
            <h3 className="mt-3 mb-1.5 text-[15px] font-bold tracking-tight text-text-light dark:text-text-dark">
              {children}
            </h3>
          ),
          h2: ({ children }) => (
            <h3 className="mt-3 mb-1.5 text-sm font-bold tracking-tight text-text-light dark:text-text-dark">
              {children}
            </h3>
          ),
          h3: ({ children }) => (
            <h4 className="mt-2.5 mb-1 text-sm font-semibold text-text-light dark:text-text-dark">
              {children}
            </h4>
          ),
          h4: ({ children }) => (
            <h5 className="mt-2 mb-1 text-xs font-semibold uppercase tracking-wide text-subtle-light dark:text-subtle-dark">
              {children}
            </h5>
          ),

          // ── Lists ─────────────────────────────────────────────────────────
          ul: ({ children }) => (
            <ul className="my-2 pl-4 space-y-1 marker:text-primary/70 dark:marker:text-secondary/70 list-disc">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2 pl-4 space-y-1 marker:text-subtle-light dark:marker:text-subtle-dark list-decimal">
              {children}
            </ol>
          ),
          li: ({ children }) => <li className="pl-0.5 leading-relaxed">{children}</li>,

          // ── Quote ─────────────────────────────────────────────────────────
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l-2 border-primary/40 dark:border-secondary/40 pl-3 italic text-subtle-light dark:text-subtle-dark">
              {children}
            </blockquote>
          ),

          // ── Code ──────────────────────────────────────────────────────────
          code: ({ className, children }) =>
            className?.includes('language-') ? (
              <code className={className}>{children}</code>
            ) : (
              <code className="px-1.5 py-0.5 rounded-md bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary text-[0.8em] font-mono">
                {children}
              </code>
            ),
          pre: ({ children }) => (
            <pre className="my-2 p-3 rounded-xl bg-neutral text-slate-100 overflow-x-auto text-xs leading-relaxed font-mono">
              {children}
            </pre>
          ),

          // ── Tables — rounded card, shaded header, zebra rows ─────────────
          table: ({ children }) => (
            <div className="my-2.5 overflow-x-auto rounded-lg border border-border-light dark:border-border-dark">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark">
              {children}
            </thead>
          ),
          tbody: ({ children }) => (
            <tbody className="[&>tr:nth-child(even)]:bg-container-light/40 dark:[&>tr:nth-child(even)]:bg-container-dark/40">
              {children}
            </tbody>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-border-light dark:border-border-dark last:border-0">
              {children}
            </tr>
          ),
          th: ({ children }) => (
            <th className="px-2.5 py-1.5 text-left font-semibold align-top whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => <td className="px-2.5 py-1.5 align-top">{children}</td>,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
